import React from 'react';
import { isNil } from 'lodash';

import type { DisplaySpecField } from '~/components/AiChat/types';
import Chip from '~/components/core/Atomic/Chip/Chip';
import { Text } from '~/components/core/TextComponents';
import { useCurrencyFormatter } from '~/components/CurrencyFormatterContext';
import { CancelIcon, CheckIcon } from '~/components/icons';
import { isoDateToUs, localTimeToViewTime } from '~/DateTimeUtils';
import BOOL4_OPTIONS from '~/server_shared/generated-types/BOOL4_OPTIONS';
import colors from '~/theme/tailwind/colors';
import cn from '~/Utils/cn';

const EMPTY_PLACEHOLDER = '—';

// BOOL4_OPTIONS is `{ yes, no, na, unknown }`; widen for lookup by an arbitrary structured-output value.
const BOOL4_LABELS: Record<string, string> = { ...BOOL4_OPTIONS };

// Semantic colors for the bool4 (`yes_no_na_unknown`) chip, applied via className because the
// design-system `Chip` has no semantic color prop (same theme tokens as ComplexityBadge in this
// view). N/A is a neutral gray; the key-value renderer keeps rows on a white background (no zebra),
// so the gray chip never blends into a row.
const BOOL4_CHIP_CLASSNAMES: Record<string, string> = {
  yes: 'bg-green-100 text-green-700',
  no: 'bg-red-100 text-red-700',
  na: 'bg-slate-200 text-slate-800',
  unknown: 'bg-yellow-100 text-yellow-800',
};

export const isEmptyValue = (value: unknown): boolean =>
  isNil(value) || value === '' || (Array.isArray(value) && value.length === 0);

export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Resolve a select/multiselect value to its configured option label, falling back to the raw value.
const optionLabel = (field: DisplaySpecField, value: unknown): string =>
  field.options?.find((option) => option.id === String(value))?.desc ?? String(value);

export const EmptyValue: React.FC = () => (
  <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
    {EMPTY_PLACEHOLDER}
  </Text>
);

/**
 * Plain-text rendering of any structured-output value, used where a composite value cannot be laid
 * out structurally (inside a table cell, deeper than one nested level, or when the value's shape
 * disagrees with its field type). Uses the spec's child labels when known, else the raw keys.
 */
export const collapseToText = (value: unknown, fields?: DisplaySpecField[] | null): string => {
  if (isEmptyValue(value)) {
    return EMPTY_PLACEHOLDER;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    return value.map((item) => collapseToText(item, fields)).join('\n');
  }
  if (isPlainObject(value)) {
    // [label, value, that value's own child fields]
    const entries: [string, unknown, DisplaySpecField[] | null | undefined][] = fields
      ? fields.map((child) => [child.label, value[child.key], child.fields])
      : Object.entries(value).map(([key, item]) => [key, item, undefined]);
    const text = entries
      .filter(([, item]) => !isEmptyValue(item))
      .map(([label, item, childFields]) => `${label}: ${collapseToText(item, childFields)}`)
      .join('; ');

    // An object whose every entry is empty reads as empty, not as a blank string.
    return text || EMPTY_PLACEHOLDER;
  }

  return String(value);
};

const ValueText: React.FC<{ children: NonNullable<React.ReactNode>; multiline?: boolean }> = ({
  children,
  multiline,
}) => (
  <Text
    variant={Text.VARIANTS.SM}
    weight={Text.WEIGHTS.MEDIUM}
    colorVariant={Text.COLOR_VARIANTS.BLACK}
    className={multiline ? 'whitespace-pre-wrap' : undefined}
  >
    {children}
  </Text>
);

/**
 * Opinionated, container-independent rendering of one structured-output value, driven by the
 * field's `type` (a ConfiguredFieldType value). Reused by any structured-output render component,
 * so it knows nothing about the surrounding layout. Empty / missing values render a muted dash.
 */
export const FieldValue: React.FC<{ field: DisplaySpecField; value: unknown }> = ({ field, value }) => {
  const { currencyFormatter } = useCurrencyFormatter();

  if (isEmptyValue(value)) {
    return <EmptyValue />;
  }

  switch (field.type) {
    case 'boolean':
      return value ? (
        <CheckIcon role="img" aria-label="Yes" size={18} iconColor={colors.green[700]} />
      ) : (
        <CancelIcon role="img" aria-label="No" size={18} iconColor={colors.red[700]} />
      );
    case 'yes_no_na_unknown': {
      const bool4Value = String(value);
      // The domain is constrained to `{yes,no,na,unknown}` upstream, so this is defensive only:
      // an out-of-domain value falls back to the `unknown` amber style (semantically "unrecognized")
      // rather than the `na` gray, so an unexpected value never masquerades as a genuine N/A.
      return (
        <Chip
          size="small"
          className={cn('h-20 rounded-full', BOOL4_CHIP_CLASSNAMES[bool4Value] ?? BOOL4_CHIP_CLASSNAMES.unknown)}
          label={BOOL4_LABELS[bool4Value] ?? bool4Value}
        />
      );
    }
    case 'multiselect': {
      const values: unknown[] = Array.isArray(value) ? value : [value];
      return (
        <div className="flex flex-wrap gap-4">
          {values.map((item, index) => (
            <Chip key={`${String(item)}-${index}`} size="small" label={optionLabel(field, item)} />
          ))}
        </div>
      );
    }
    case 'select':
      return <ValueText>{optionLabel(field, value)}</ValueText>;
    case 'monetary': {
      const numValue = typeof value === 'number' ? value : Number(value);
      return <ValueText>{!Number.isNaN(numValue) ? currencyFormatter.format(numValue) : String(value)}</ValueText>;
    }
    case 'date':
      return <ValueText>{isoDateToUs(String(value))}</ValueText>;
    case 'time':
      return <ValueText>{localTimeToViewTime(String(value))}</ValueText>;
    case 'multiline_string':
      return <ValueText multiline>{String(value)}</ValueText>;
    default:
      // Field types with no explicit case above (e.g. `location`, `contact`) can still reach the
      // table — `resolve_insight_display_spec` is expert-agnostic, so any configured-field type may
      // appear in an override. Object-valued types would stringify to `[object Object]`; render the
      // empty placeholder instead. Primitive values (`number`, `us_state_select`, …) render as-is.
      return typeof value === 'object' ? <EmptyValue /> : <ValueText>{String(value)}</ValueText>;
  }
};

// An object, or a list holding one at any depth: a shape no scalar field type describes.
const holdsObject = (value: unknown): boolean =>
  isPlainObject(value) || (Array.isArray(value) && value.some(holdsObject));

interface CollapsedTextProps {
  field: DisplaySpecField;
  value: unknown;
  // As on `KeyValueRow`. Never in a table cell: a lower min-content width there squeezes the column
  // into mid-word breaks where the table should scroll instead.
  wrapAnywhere?: boolean;
}

export const CollapsedText: React.FC<CollapsedTextProps> = ({ field, value, wrapAnywhere }) =>
  isEmptyValue(value) ? (
    <EmptyValue />
  ) : (
    <Text
      variant={Text.VARIANTS.SM}
      weight={Text.WEIGHTS.MEDIUM}
      colorVariant={Text.COLOR_VARIANTS.BLACK}
      className={cn('whitespace-pre-wrap', wrapAnywhere && 'wrap-anywhere')}
    >
      {collapseToText(value, field.fields)}
    </Text>
  );

// A scalar-typed value holding an object collapses to text: `FieldValue`'s typed branches would print it
// as `[object Object]`.
export const ScalarValue: React.FC<{ field: DisplaySpecField; value: unknown }> = ({ field, value }) =>
  holdsObject(value) ? <CollapsedText field={field} value={value} /> : <FieldValue field={field} value={value} />;
